import { useState, useEffect } from "react";
import { Check, Loader2, CreditCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

export default function PaymentSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-10" />;

  const p = s.payment || {};
  const set = (k, v) => setS((prev) => ({ ...prev, payment: { ...prev.payment, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { payment: {
        moyasar_enabled: p.moyasar_enabled, mode: p.mode, publishable_key: p.publishable_key,
        methods: p.methods, default_payment_status: p.default_payment_status,
      } });
      toast.success("تم حفظ إعدادات الدفع");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggleMethod = (m) => {
    const cur = p.methods || [];
    set("methods", cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">بوابة الدفع (Moyasar)</h1>
        <Button onClick={save} disabled={saving} className="rounded-full font-semibold" data-testid="save-payment-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Check className="h-4 w-4 me-1" />} حفظ
        </Button>
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-5 flex gap-3 text-sm text-amber-800">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <p>المفتاح السري (Secret Key) يُحفظ على الخادم فقط في متغيرات البيئة، ولا يظهر أبداً في الواجهة. فعّلي البوابة بعد اعتماد حساب Moyasar وإضافة المفتاح السري على الخادم.</p>
      </div>

      <section className="rounded-2xl bg-card cb-card-shadow p-5 space-y-4">
        <label className="flex items-center justify-between py-1">
          <span className="font-semibold flex items-center gap-2"><CreditCard className="h-5 w-5" /> تفعيل الدفع الإلكتروني</span>
          <Switch checked={!!p.moyasar_enabled} onCheckedChange={(v) => set("moyasar_enabled", v)} data-testid="toggle-moyasar" />
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="mb-1 block text-xs">الوضع</Label>
            <Select value={p.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="rounded-xl h-11" data-testid="moyasar-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="test">اختبار (Test)</SelectItem><SelectItem value="live">مباشر (Live)</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs">حالة الدفع الافتراضية</Label>
            <Select value={p.default_payment_status} onValueChange={(v) => set("default_payment_status", v)}>
              <SelectTrigger className="rounded-xl h-11" data-testid="default-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="pay_at_salon">الدفع في الصالون</SelectItem><SelectItem value="unpaid">غير مدفوع</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="mb-1 block text-xs">المفتاح العام (Publishable Key)</Label>
          <Input value={p.publishable_key || ""} onChange={(e) => set("publishable_key", e.target.value)} className="rounded-xl h-11" dir="ltr" placeholder="pk_test_..." data-testid="publishable-key" />
        </div>

        <div>
          <Label className="mb-2 block text-xs">وسائل الدفع</Label>
          <div className="flex flex-wrap gap-2">
            {["creditcard", "applepay"].map((m) => (
              <button key={m} onClick={() => toggleMethod(m)} data-testid={`method-${m}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 ${(p.methods || []).includes(m) ? "border-primary bg-secondary/40" : "border-border"}`}>
                {m === "creditcard" ? "بطاقات (مدى/فيزا)" : "Apple Pay"}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
